/**
 * Focus One inventory filter panel.
 *
 * Phones get a full-screen sheet with a pinned "Show N vehicles" footer; lg and up keeps the
 * persistent sidebar, which can now be collapsed to a rail and remembers that choice.
 *
 * Long option lists (Make especially) are capped with a "Show all" expander and, past a larger
 * threshold, a search box — which is the part that actually fixes the endless-scrolling problem.
 *
 * Filters still apply immediately on change, exactly as before. The footer button reports the
 * count and closes the sheet; it is deliberately not an "Apply" that defers changes, so none of
 * handle-filters.ts's existing behaviour is altered.
 */
const shell = document.querySelector<HTMLElement>('[data-filters-shell]');

if (shell) {
    const layout = document.querySelector<HTMLElement>('[data-inventory-layout]');
    const body = shell.querySelector<HTMLElement>('[data-filters-body]');
    const openButton = document.querySelector<HTMLElement>('[data-filters-open]');
    const countBadge = document.querySelector<HTMLElement>('[data-filters-count]');
    const appliedList = document.getElementById('applied-filters-list');

    const COLLAPSE_KEY = 'fo-filters-collapsed';
    const VISIBLE_CAP = 8;
    const SEARCH_THRESHOLD = 12;

    let lockedScrollY = 0;

    // ---------- sheet (phones) ----------
    const openSheet = () => {
        lockedScrollY = window.scrollY;
        shell.classList.add('is-sheet-open');
        document.body.style.position = 'fixed';
        document.body.style.top = `-${lockedScrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        shell.querySelector<HTMLElement>('[data-filters-close]')?.focus();
    };

    const closeSheet = () => {
        if (shell.classList.contains('is-sheet-open') === false) return;
        shell.classList.remove('is-sheet-open');
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        window.scrollTo(0, lockedScrollY);
        openButton?.focus();
    };

    openButton?.addEventListener('click', openSheet);
    shell.querySelector('[data-filters-close]')?.addEventListener('click', closeSheet);

    shell.querySelector('[data-filters-apply]')?.addEventListener('click', () => {
        closeSheet();
        document.getElementById('inventory-results-region')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    // Reuses the existing Clear All rather than duplicating its id or its logic.
    shell.querySelector('[data-filters-clear]')?.addEventListener('click', () => {
        document.getElementById('clear-all-filters')?.click();
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeSheet();
    });

    // A sheet left open while the window widens would otherwise pin the body scroll for good.
    window.addEventListener('resize', () => {
        if (window.innerWidth >= 1024) closeSheet();
    });

    // ---------- collapse (lg and up) ----------
    const collapseButton = shell.querySelector<HTMLElement>('[data-filters-collapse]');

    const setCollapsed = (collapsed: boolean) => {
        shell.classList.toggle('is-collapsed', collapsed);
        layout?.classList.toggle('is-filters-collapsed', collapsed);
        collapseButton?.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
        collapseButton?.setAttribute('aria-label', collapsed ? 'Expand filters' : 'Collapse filters');
    };

    try {
        if (window.localStorage.getItem(COLLAPSE_KEY) === '1') setCollapsed(true);
    } catch {
        /* storage blocked — the sidebar simply starts expanded */
    }

    collapseButton?.addEventListener('click', () => {
        const collapsed = shell.classList.contains('is-collapsed') === false;
        setCollapsed(collapsed);
        try {
            window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
        } catch {
            /* nothing to do — the toggle still works for this page view */
        }
    });

    // ---------- active filter count on the trigger ----------
    const refreshCount = () => {
        if (!countBadge || !appliedList) return;
        const count = appliedList.children.length;
        countBadge.textContent = count > 0 ? String(count) : '';
        countBadge.classList.toggle('hidden', count === 0);
    };

    if (appliedList) {
        new MutationObserver(refreshCount).observe(appliedList, { childList: true });
        refreshCount();
    }

    // ---------- long option lists ----------
    const labelTextOf = (option: HTMLElement) => (option.textContent ?? '').replace(/\s+/g, ' ').trim().toLowerCase();

    const groupsOf = (options: HTMLElement[]) => {
        const groups = new Map<HTMLElement, HTMLElement[]>();
        options.forEach((option) => {
            const parent = option.parentElement;
            if (!parent) return;
            const bucket = groups.get(parent) ?? [];
            bucket.push(option);
            groups.set(parent, bucket);
        });
        return groups;
    };

    // Plural shown on the expander, e.g. "Show all 63 models", so a make's model list and the make
    // list itself never read the same once both are open.
    const GROUP_NOUNS: Record<string, string> = { Make: 'makes', Model: 'models', Trim: 'trims', Color: 'colors' };

    // Only the group's own options: a selected make holds its models (and a model its trims) inside
    // its option element, and those belong to their own nested group.
    const ownOptionsOf = (parent: HTMLElement) =>
        Array.from(parent.children).filter((child): child is HTMLElement => child instanceof HTMLElement && child.matches('[data-filter-option]'));

    const enhanceGroup = (parent: HTMLElement, options: HTMLElement[]) => {
        parent.dataset.foEnhanced = 'true';

        // Selected options first, so a choice never scrolls out of reach.
        const selected = options.filter((o) => o.querySelector<HTMLInputElement>(':scope > input')?.checked);
        selected.reverse().forEach((option) => parent.prepend(option));

        const ordered = ownOptionsOf(parent);
        const noun = GROUP_NOUNS[ordered[0]?.dataset.filterName ?? ''];
        let expanded = false;

        const apply = (term: string) => {
            const needle = term.trim().toLowerCase();
            let shown = 0;

            ordered.forEach((option) => {
                const matches = needle.length === 0 || labelTextOf(option).includes(needle);
                const withinCap = expanded || needle.length > 0 || shown < VISIBLE_CAP;
                const visible = matches && withinCap;
                option.classList.toggle('fo-opt-hidden', visible === false);
                if (matches) shown += 1;
            });

            if (moreButton) {
                const hiddenCount = ordered.length - VISIBLE_CAP;
                moreButton.classList.toggle('fo-opt-hidden', needle.length > 0 || hiddenCount <= 0);
                moreButton.textContent = noun
                    ? (expanded ? `Show fewer ${noun}` : `Show all ${ordered.length} ${noun}`)
                    : (expanded ? 'Show less' : `Show all ${ordered.length}`);
            }

            if (emptyNote) emptyNote.classList.toggle('fo-opt-hidden', shown > 0);
        };

        let moreButton: HTMLButtonElement | null = null;
        let emptyNote: HTMLElement | null = null;

        if (ordered.length > SEARCH_THRESHOLD) {
            const search = document.createElement('input');
            search.type = 'search';
            search.className = 'fo-filter-search';
            search.placeholder = 'Search…';
            search.setAttribute('aria-label', 'Search filter options');
            search.addEventListener('input', () => apply(search.value));
            parent.prepend(search);

            emptyNote = document.createElement('p');
            emptyNote.className = 'fo-filter-empty fo-opt-hidden';
            emptyNote.textContent = 'No matches';
            parent.appendChild(emptyNote);
        }

        moreButton = document.createElement('button');
        moreButton.type = 'button';
        moreButton.className = 'fo-filter-more';
        moreButton.addEventListener('click', () => {
            expanded = !expanded;
            apply('');
            const search = parent.querySelector<HTMLInputElement>(':scope > .fo-filter-search');
            if (search) search.value = '';
        });
        parent.appendChild(moreButton);

        apply('');
    };

    const enhanceLongGroups = (options: HTMLElement[]) => {
        groupsOf(options).forEach((group, parent) => {
            if (parent.dataset.foEnhanced !== 'true' && group.length > VISIBLE_CAP) enhanceGroup(parent, group);
        });
    };

    enhanceLongGroups(Array.from(body?.querySelectorAll<HTMLElement>('[data-filter-option]') ?? []));

    // handle-filters.ts inserts a make's models (and a model's trims) only when it is ticked, so
    // those lists arrive after page load and get the same cap here instead of opening in full.
    if (body) {
        new MutationObserver((mutations) => {
            const added: HTMLElement[] = [];
            mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
                if (!(node instanceof HTMLElement)) return;
                if (node.matches('[data-filter-option]')) added.push(node);
                added.push(...Array.from(node.querySelectorAll<HTMLElement>('[data-filter-option]')));
            }));
            if (added.length > 0) enhanceLongGroups(added);
        }).observe(body, { childList: true, subtree: true });
    }
}
